import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { BehaviorSubject, map, take, tap } from 'rxjs';
import {
  IColumnCardResponse,
  IColumnCardsResponse,
  IColumnResponse,
  IColumnsResponse,
  IDeleteResponse,
  IFunnelResponse,
  IFunnelsResponse,
  IGroupResponse,
  IGroupsResponse,
  IInvitersResponse,
  IManagersResponse,
  IPermissionsResponse,
} from '../interfaces';
import { ActivityService } from '../components/activity/activity.service';
import { IParams } from '../model/http';
import { PERMISSIONS, USER_COMPANY_STATUS } from '../enum/constants';


export const initItemsInvParams: IParams = {
  sort: '',
  order: '',
  search: '',
  take: 25,
  page: 1,
};

export const initItemsManParams: IParams = {
  sort: '',
  order: '',
  search: '',
  take: 25,
  page: 1,
};

export const initItemsGroParams: IParams = {
  sort: '',
  order: '',
  search: '',
  take: 25,
  page: 1,
};

interface IConfig {
  ai_token_price: number;
  liqpay_private_key: string;
  liqpay_public_key: string;
  default_ai_prompt: string;
  is_olx_statistics: boolean;
  is_new_year: boolean;
}

export interface EmployeeProfile {
  id: string;
  membership_id?: string;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  phone?: string | null;
  avatar_url?: string | null;
  group_id?: string | null;
  group?: string | null;
  role?: string | null;
  membership_status?: USER_COMPANY_STATUS | null;
  /** users.status: 'online' | 'offline' */
  status?: string | null;
}

export interface BulkInviteEmployeesRequest {
  managers: { email: string; phone: string; first_name: string; last_name: string; group_id: string }[];
  is_register: boolean;
  is_skip_email: boolean;
}

export interface BulkInviteEmployeesResponse {
  items: { email: string; success: boolean; error?: { code: string; message: string } }[];
  success_count: number;
  failed_count: number;
}

@Injectable({
  providedIn: 'root',
})
export class CrmService {

  private urlCrmSetting = `${environment.baseURL}/crm`;

  public readonly isLoader$ = new BehaviorSubject<boolean>(false);

  private readonly itemsInvitersSubject$ = new BehaviorSubject<any>({});
  public readonly itemsInviters$ = this.itemsInvitersSubject$.asObservable();

  private readonly itemsManagersSubject$ = new BehaviorSubject<any>({});
  public readonly itemsManagers$ = this.itemsManagersSubject$.asObservable();

  private readonly itemsGroupsSubject$ = new BehaviorSubject<any>({});
  public readonly itemsGroups$ = this.itemsGroupsSubject$.asObservable();

  public queryInvitersParams = { ...initItemsInvParams };
  public queryManagersParams = { ...initItemsManParams };
  public queryGroupsParams = { ...initItemsGroParams };

  public config!: IConfig;

  constructor(
    private readonly http: HttpClient,
    private readonly activityService: ActivityService,
  ) {
  }

  public getConfig() {
    return this.http.get<number>(`${this.urlCrmSetting}`, {})
      .pipe(map((v: any) => v.data), tap(v => this.config = v as IConfig));
  }

  public getDollar() {
    return this.http.get<number>(`${this.urlCrmSetting}/dollar`, {})
      .pipe(map((v: any) => v.data));
  }

  public getAllFunnels(params: any) {
    return this.http.get<IFunnelsResponse>(environment.baseURL + '/funnels', { params })
      .pipe(map(v => v.data));
  }

  public getDefaultFunnel(params: any) {
    return this.http.get<IFunnelResponse>(environment.baseURL + '/funnels/default', { params })
      .pipe(map(v => v.data));
  }

  public getAllColumns(params: any) {
    return this.http.get<IColumnsResponse>(environment.baseURL + '/columns', { params })
      .pipe(map(v => v.data));
  }

  public getAllColumnsCards(body: any) {
    return this.http.post<any>(environment.baseURL + '/cards/list', body)
      .pipe(map(v => ({ ...v.data, column_id: body.column_id })));
  }


  public getFunnelById(funnel_id: string) {
    const url: string = environment.baseURL + `/funnels/${funnel_id}`;
    return this.http.get<IFunnelResponse>(url).pipe(map(v => v.data));
  }

  public getColumnById(column_id: string) {
    const url: string = environment.baseURL + `/columns/${column_id}`;
    return this.http.get<IColumnResponse>(url).pipe(map(v => v.data));
  }

  public getColumnCardById(card_id: string) {
    const url: string = environment.baseURL + `/cards/${card_id}`;
    return this.http.get<IColumnCardResponse>(url).pipe(map(v => v.data));
  }

  public getCard(card_id: string) {
    const url: string = environment.baseURL + `/cards/${card_id}`;
    return this.http.get<IColumnCardResponse>(url)
      .pipe(map(v => v.data));
  }


  public createFunnel(payload: any) {
    return this.http.post<IFunnelsResponse>(environment.baseURL + '/funnels', payload)
      .pipe(map(v => v.data));
  }

  public createColumn(payload: any) {
    return this.http.post<IColumnsResponse>(environment.baseURL + '/columns', payload)
      .pipe(map(v => v.data));
  }

  public createColumnCard(payload: any) {
    return this.http.post<IColumnCardsResponse>(environment.baseURL + '/cards', payload)
      .pipe(map(v => v.data));
  }

  public duplicationCard(payload: any) {
    return this.http.post<IColumnCardsResponse>(environment.baseURL + '/cards/copy', payload)
      .pipe(map(v => v.data));
  }


  public updateFunnel(payload: any, funnel_id: string) {
    const url: string = environment.baseURL + `/funnels/${funnel_id}`;
    return this.http.put<IFunnelsResponse>(url, payload)
      .pipe(map(v => v.data));
  }

  public updateColumn(payload: any, column_id: string) {
    const url: string = environment.baseURL + `/columns/${column_id}`;
    return this.http.put<IColumnsResponse>(url, payload)
      .pipe(map(v => v.data));
  }

  public updateCard(card_id: string, payload: any, ) {
    const url: string = environment.baseURL + `/cards/${card_id}`;
    return this.http.put<IColumnCardResponse>(url, payload)
      .pipe(map(v => v.data), tap(card => {
        this.activityService.loadItems(card.id, card.funnel.type)
          .pipe(take(1))
          .subscribe();
      }));
  }


  public deleteFunnel(funnel_id: string) {
    const url: string = environment.baseURL + `/funnels/${funnel_id}`;
    return this.http.delete<IDeleteResponse>(url).pipe(map(v => v.data));
  }

  public deleteColumn(column_id: string) {
    const url: string = environment.baseURL + `/columns/${column_id}`;
    return this.http.delete<IDeleteResponse>(url).pipe(map(v => v.data));
  }

  public deleteColumnCards(ids: string[]) {
    const payload = { ids };
    return this.http.post<IDeleteResponse>(environment.baseURL + '/cards/delete', payload)
      .pipe(map(v => v.data));
  }

  public updateColumnCardCA(payload: any, card_id: string) {
    const url: string = environment.baseURL + `/cards/${card_id}/ca`;
    return this.http.put<IColumnCardResponse>(url, payload)
      .pipe(map(v => v.data));
  }

  public updateColumnCardCABulk(payload: any, card_id: string) {
    const url: string = environment.baseURL + `/cards/${card_id}/ca/bulk`;
    return this.http.put<IColumnCardResponse>(url, payload)
      .pipe(map(v => v.data));
  }


  public sendInvite(payload: any) {
    const url: string = environment.baseURL + '/users/invite';
    return this.http.post<any>(url, payload)
      .pipe(map(v => v.data));
  }

  public inviteEmployees(payload: BulkInviteEmployeesRequest) {
    return this.http.post<any>(environment.baseURL + '/employees/invites', payload)
      .pipe(map(v => v.data as BulkInviteEmployeesResponse));
  }

  public resendInvite(payload: any, invite_id: string) {
    const url: string = environment.baseURL + `/users/invite/resend/${invite_id}`;
    return this.http.post<any>(url, payload)
      .pipe(map(v => v.data));
  }

  public cancelInvite(payload: any, invite_id: string) {
    const url: string = environment.baseURL + `/users/invite/cancel/${invite_id}`;
    return this.http.post<any>(url, payload)
      .pipe(map(v => v.data));
  }


  public updateManager(payload: any, manager_id: string) {
    return this.http.put<any>(environment.baseURL + `/employees/${manager_id}`, payload)
      .pipe(map(v => v.data));
  }

  public bulkUpdateManagers(payload: any) {
    return this.http.put<any>(environment.baseURL + '/employees', payload)
      .pipe(map(v => v.data));
  }

  public deleteManager(manager_id: string) {
    return this.http.delete<any>(environment.baseURL + `/employees/${manager_id}`);
  }

  public bulkDeleteManager(managers: string[]) {
    return this.http.put<any>(environment.baseURL + '/employees/delete', { employees: managers });
  }

  public logoutManager(manager_id: string) {
    const url: string = environment.baseURL + `/employees/${manager_id}/logout`;
    return this.http.post<any>(url, {});
  }

  public suspendEmployee(employee_id: string, new_manager_id?: string | null) {
    return this.http.post<any>(environment.baseURL + `/employees/${employee_id}/suspend`, new_manager_id ? { new_manager_id } : {});
  }

  public restoreEmployee(employee_id: string) {
    return this.http.post<any>(environment.baseURL + `/employees/${employee_id}/restore`, {});
  }

  /** Takes the user_companies id (`membership_id`), not the user id */
  public resendEmployeeInvite(membership_id: string) {
    return this.http.post<any>(environment.baseURL + `/employees/invites/${membership_id}/resend`, {});
  }

  /** Takes the user_companies id (`membership_id`), not the user id */
  public cancelEmployeeInvite(membership_id: string) {
    return this.http.post<any>(environment.baseURL + `/employees/invites/${membership_id}/cancel`, {});
  }

  public createGroup(payload: any) {
    return this.http.post<any>(environment.baseURL + '/groups', payload)
      .pipe(map(v => v.data));
  }

  public updateGroup(payload: any, group_id: string) {
    return this.http.put<any>(environment.baseURL + `/groups/${group_id}`, payload)
      .pipe(map(v => v.data));
  }

  public deleteGroup(group_id: string) {
    return this.http.delete<any>(environment.baseURL + `/groups/${group_id}`);
  }


  public loadListInviters() {
    this.isLoader$.next(true);
    return this.getAllInviters(initItemsInvParams)
      .pipe(tap((v) => {
        this.itemsInvitersSubject$.next(v);
        this.isLoader$.next(false);
      }));
  }

  public loadListManagers() {
    this.isLoader$.next(true);
    return this.getAllManagers(initItemsManParams)
      .pipe(tap((v) => {
        this.itemsManagersSubject$.next(v);
        this.isLoader$.next(false);
      }));
  }

  public loadListGroups() {
    this.isLoader$.next(true);
    return this.getAllGroups(initItemsGroParams)
      .pipe(tap((v) => {
        this.itemsGroupsSubject$.next(v);
        this.isLoader$.next(false);
      }));
  }

  public getAllInviters(params: any) {
    const url: string = environment.baseURL + '/users/invites';
    return this.http.get<IInvitersResponse>(url, { params: { ...params } })
      .pipe(map((v: any) => v.data));
  }

  public getManagerById(manager_id: string) {
    const url: string = environment.baseURL + `/employees/${manager_id}`;
    return this.http.get<IGroupResponse>(url)
      .pipe(map((v: any) => v.data));
  }

  public getAllManagers(params: any) {
    const url: string = environment.baseURL + '/employees';
    return this.http.get<IManagersResponse>(url, { params: { ...params } })
      .pipe(map((v: any) => v.data));
  }

  public getGroupById(group_id: string) {
    const url: string = environment.baseURL + `/groups/${group_id}`;
    return this.http.get<IGroupResponse>(url)
      .pipe(map((v: any) => v.data));
  }

  public getAllGroups(params: any) {
    const url: string = environment.baseURL + '/groups';
    return this.http.get<IGroupsResponse>(url, { params: { ...params } })
      .pipe(map((v: any) => v.data));
  }

  public getAllPermissions() {
    const url: string = environment.baseURL + '/groups/permissions';
    return this.http.get<IPermissionsResponse>(url)
      .pipe(map((v: any) => v.data));
  }

  public getAllPermissionsByGroupId(group_id: string) {
    const url: string = environment.baseURL + `/groups/${group_id}/permissions`;
    return this.http.get<IPermissionsResponse>(url)
      .pipe(map((v: any) => v.data));
  }


  public loadInviters() {
    this.isLoader$.next(true);
    return this.getAllInviters(this.queryInvitersParams)
      .pipe(tap((v) => {
        this.itemsInvitersSubject$.next(v);
        this.isLoader$.next(false);
      }));
  }

  public setQueryInvitersParams(params: IParams) {
    const {
      sort,
      order,
      search,
      take,
      page,
    } = params;

    this.queryInvitersParams = {
      sort: sort || typeof (sort) === 'string' ? sort : this.queryInvitersParams.sort,
      order: order || typeof (order) === 'string' ? order : this.queryInvitersParams.order,
      search: search || typeof (search) === 'string' ? search : this.queryInvitersParams.search,
      take: take || this.queryInvitersParams.take,
      page: page || this.queryInvitersParams.page,
    };

    return this.loadInviters();
  }

  public loadManagers() {
    this.isLoader$.next(true);
    return this.getAllManagers(this.queryManagersParams)
      .pipe(tap((v) => {
        this.itemsManagersSubject$.next(v);
        this.isLoader$.next(false);
      }));
  }

  /** Live presence update for the employees table from the `manager:status` socket event */
  public patchManagerPresence(managerId: string, status: string, lastActiveAt?: string): void {
    const current = this.itemsManagersSubject$.getValue();
    if (!Array.isArray(current?.items) || !current.items.some((item: any) => item.id === managerId)) return;

    this.itemsManagersSubject$.next({
      ...current,
      items: current.items.map((item: any) =>
        item.id === managerId ? { ...item, status, last_active_at: lastActiveAt ?? item.last_active_at } : item,
      ),
    });
  }

  public getManagersSnapshot(): any {
    return this.itemsManagersSubject$.getValue();
  }

  public setManagersItems(data: any): void {
    this.itemsManagersSubject$.next(data);
    this.isLoader$.next(false);
  }

  public setQueryManagersParams(params: IParams) {
    const {
      sort,
      order,
      search,
      take,
      page,
    } = params;

    this.queryManagersParams = {
      sort: sort || typeof (sort) === 'string' ? sort : this.queryManagersParams.sort,
      order: order || typeof (order) === 'string' ? order : this.queryManagersParams.order,
      search: search || typeof (search) === 'string' ? search : this.queryManagersParams.search,
      take: take || this.queryManagersParams.take,
      page: page || this.queryManagersParams.page,
    };

    return this.loadManagers();
  }

  public loadGroups() {
    this.isLoader$.next(true);
    return this.getAllGroups(this.queryGroupsParams)
      .pipe(tap((v) => {
        this.itemsGroupsSubject$.next(v);
        this.isLoader$.next(false);
      }));
  }

  public setQueryGroupsParams(params: IParams) {
    const {
      sort,
      order,
      search,
      take,
      page,
    } = params;

    this.queryGroupsParams = {
      sort: sort || typeof (sort) === 'string' ? sort : this.queryGroupsParams.sort,
      order: order || typeof (order) === 'string' ? order : this.queryGroupsParams.order,
      search: search || typeof (search) === 'string' ? search : this.queryGroupsParams.search,
      take: take || this.queryGroupsParams.take,
      page: page || this.queryGroupsParams.page,
    };

    return this.loadGroups();
  }

  public checkPermission(key: PERMISSIONS[]) {
    const url: string = environment.baseURL + '/groups/permission';
    const payload: any = {
      key,
    };

    return this.http.post<any>(url, payload).pipe(map((v: any) => v.data));
  }

  public getAllFilters(type: string) {
    return this.http.get<any>(environment.baseURL + '/filter', { params: { type } }).pipe(map((v: any) => v.data));
  }

  public saveFilter(payload: any) {
    return this.http.post<any>(environment.baseURL + '/filter', payload).pipe(map((v: any) => v.data));
  }

  public updateFilter(payload: any) {
    return this.http.put<any>(environment.baseURL + `/filter/${payload.id}`, payload);
  }

  public deleteFilter(id: any) {
    return this.http.delete<any>(environment.baseURL + `/filter/${id}`);
  }
}
